import React, { useState, useEffect, useRef, useCallback } from 'react';
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { FaStar } from "react-icons/fa";
import "./Testimonial.css";
// Assuming you have a placeholder or actual images for Pakistani users
import userImage from "../../../Assets/Users/12225935.png";

const Testimonial = () => {
  // --- Updated testimonials for "Zircon Jewels" with Pakistani names ---
  const testimonials = [
    {
      id: 1,
      name: "Aisha Khan",
      role: "Verified Buyer",
      content: "The craftsmanship of my necklace from Zircon Jewels is simply breathtaking. It was the perfect piece for the wedding I attended. I received so many compliments!",
      rating: 5,
      image: userImage
    },
    {
      id: 2,
      name: "Bilal Ahmed",
      role: "Verified Buyer",
      content: "I bought a pair of earrings for my wife's birthday, and she was overjoyed. The design is elegant and unique, and the quality is outstanding. Thank you, Zircon Jewels!",
      rating: 5,
      image: userImage
    },
    {
      id: 3,
      name: "Fatima Raza",
      role: "Jewelry Enthusiast",
      content: "As someone who adores fine jewelry, I'm so impressed with Zircon Jewels. Their collection has a timeless appeal, blending traditional motifs with modern style perfectly.",
      rating: 4,
      image: userImage
    },
    {
      id: 4,
      name: "Usman Malik",
      role: "Verified Buyer",
      content: "The online shopping experience was seamless, and the delivery was prompt. The ring I ordered exceeded my expectations. It's even more beautiful in person. Highly recommended!",
      rating: 5,
      image: userImage
    }
  ];

  // --- Slider Logic ---
  const [currentIndex, setCurrentIndex] = useState(1); // Start at the first "real" slide
  const [isTransitioning, setIsTransitioning] = useState(false);
  // State to pause auto-scroll on hover for better UX
  const [isPaused, setIsPaused] = useState(false);
  const wrapperRef = useRef(null);
  const intervalRef = useRef(null);
  const transitionDuration = 500; // Corresponds to CSS transition time

  // Memoize testimonials to prevent re-creation on every render
  const duplicatedTestimonials = React.useMemo(() => {
    if (testimonials.length === 0) return [];
    const first = testimonials[0];
    const last = testimonials[testimonials.length - 1];
    return [last, ...testimonials, first];
  }, [testimonials]);

  const totalSlides = testimonials.length;

  // --- Functions for Navigation ---
  const changeSlide = useCallback((direction) => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentIndex(prevIndex => prevIndex + direction);
    
    // Use a timeout to reset the transitioning flag after the animation completes
    setTimeout(() => {
        setIsTransitioning(false);
    }, transitionDuration);
  }, [isTransitioning]);

  const handlePrev = () => {
    changeSlide(-1);
  };
  
  const handleNext = useCallback(() => {
    changeSlide(1);
  }, [changeSlide]);

  const handleDotClick = (index) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(index + 1); // Adjust for the prepended clone
    setTimeout(() => {
        setIsTransitioning(false);
    }, transitionDuration);
  };

  // --- Effect for Infinite Loop ---
  // This effect handles the "jump" when reaching the cloned slides
  useEffect(() => {
    if (currentIndex === 0 || currentIndex === totalSlides + 1) {
      const timer = setTimeout(() => {
        if (wrapperRef.current) {
          wrapperRef.current.style.transition = 'none';
          const newIndex = currentIndex === 0 ? totalSlides : 1;
          setCurrentIndex(newIndex);
          
          // Force a reflow to apply the new index without transition
          // Then re-enable transitions for the next user interaction
          requestAnimationFrame(() => {
            if (wrapperRef.current) {
              wrapperRef.current.style.transition = `transform ${transitionDuration}ms ease-in-out`;
            }
          });
        }
      }, transitionDuration);
      
      return () => clearTimeout(timer);
    }
  }, [currentIndex, totalSlides]);

  // --- Effect for Auto-Scrolling ---
  // This now respects the isPaused state
  useEffect(() => {
    if (isPaused) {
      clearInterval(intervalRef.current);
    } else {
      intervalRef.current = setInterval(handleNext, 5000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isPaused, handleNext]);


  // --- Correct dot index calculation ---
  let activeDotIndex = currentIndex - 1;
  if (currentIndex === 0) {
    activeDotIndex = totalSlides - 1;
  } else if (currentIndex === totalSlides + 1) {
    activeDotIndex = 0;
  }

  return (
    <div className="testimonial">
      <h2>What Our Customers Say</h2>
      <div 
        className="testimonialContainer"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div 
          ref={wrapperRef} 
          className="testimonialWrapper"
          style={{ 
            transform: `translateX(-${currentIndex * 100}%)`,
            transition: `transform ${transitionDuration}ms ease-in-out`
          }}
        >
          {duplicatedTestimonials.map((testimonial, index) => (
            <div key={index} className="testimonialSlide">
              <div className="testimonialContent">
                <div className="testimonialProfileImage">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name}
                    onError={(e) => { e.target.src = "/api/placeholder/100/100"; }}
                  />
                </div>
                <div className="testimonialStars">
                  {[...Array(5)].map((_, i) => (
                    <FaStar 
                      key={i} 
                      className={i < testimonial.rating ? 'filled' : 'unfilled'} 
                    />
                  ))}
                </div>
                <p className="testimonialText">"{testimonial.content}"</p>
                <div className="testimonialAuthor">
                  <h4 className="testimonialName">{testimonial.name}</h4>
                  <p className="testimonialRole">{testimonial.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button 
          onClick={handlePrev} 
          className="testimonialNav testimonialPrev"
          aria-label="Previous testimonial"
          disabled={isTransitioning}
        >
          <IoIosArrowBack />
        </button>
        <button 
          onClick={handleNext} 
          className="testimonialNav testimonialNext"
          aria-label="Next testimonial"
          disabled={isTransitioning}
        >
          <IoIosArrowForward />
        </button>

        <div className="testimonialDots">
          {testimonials.map((_, index) => (
            <button
              key={index}
              className={`testimonialDot ${activeDotIndex === index ? 'active' : ''}`}
              onClick={() => handleDotClick(index)}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Testimonial;